import { Base } from '@studiometa/js-toolkit';

/**
 * JudgemeCarouselDots component
 *
 * Ajoute une navigation par « dots » au Cards Carousel de Judge.me
 * (`.jdgm-cards-carousel`) en remplacement des flèches natives (masquées en CSS).
 *
 * Le widget Judge.me est rendu côté client, en boucle infinie (clones
 * `.jdgm-clone-before/after`), la carte courante portant la classe `.active`
 * (`data-card-index`). La navigation officielle passe uniquement par
 * `window.jdgmNextCard(blockId)` / `window.jdgmPreviousCard(blockId)` : il
 * n'existe pas de « aller à l'index N ». On atteint donc l'index cible en
 * avançant/reculant pas à pas jusqu'à l'atteindre (auto-correctif, quel que
 * soit le pas réel du widget).
 */
export default class JudgemeCarouselDots extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'JudgemeCarouselDots',
  };

  /**
   * Build the dots UI and start syncing on the active card.
   */
  mounted() {
    this.blockId = this.$el.dataset.jdgmBlockId || this.$el.id;
    this.track = this.$el.querySelector('.jdgm-videos-container');
    this.content = this.$el.querySelector('.jdgm-content');

    if (!this.track || !this.content || !this.blockId) {
      return;
    }

    this.moving = false;
    this.dots = [];
    this.buildDots();

    // Le widget peut re-render ses cartes (chargement asynchrone, changement de
    // langue…) : on reconstruit les dots quand le nombre de cartes change, et on
    // synchronise le dot actif quand la classe `.active` se déplace.
    this.observer = new MutationObserver(() => {
      const count = this.realCards.length;
      if (count !== this.cardsCount) {
        this.buildDots();
      } else {
        this.syncActiveDot();
      }
    });
    this.observer.observe(this.track, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class'],
    });
  }

  /**
   * Clean up on unmount.
   */
  destroyed() {
    this.observer?.disconnect();
    this.nav?.remove();
  }

  /**
   * Real (non-cloned) review cards.
   * @returns {HTMLElement[]}
   */
  get realCards() {
    return Array.from(
      this.track.querySelectorAll(
        '.jdgm-carousel-item:not(.jdgm-clone-before):not(.jdgm-clone-after)',
      ),
    );
  }

  /**
   * Number of cards displayed per view, driven by the widget's own CSS var.
   * @returns {number}
   */
  get perPage() {
    const value = Number.parseInt(
      getComputedStyle(this.$el).getPropertyValue('--slides-count'),
      10,
    );
    return Number.isNaN(value) || value < 1 ? 1 : value;
  }

  /**
   * Index (`data-card-index`) of the currently active card.
   * @returns {number}
   */
  get currentIndex() {
    const active = this.track.querySelector('.jdgm-carousel-item.active');
    return active ? Number.parseInt(active.dataset.cardIndex, 10) || 0 : 0;
  }

  /**
   * (Re)build the dots navigation.
   */
  buildDots() {
    this.cardsCount = this.realCards.length;
    this.pageCount = Math.max(1, Math.ceil(this.cardsCount / this.perPage));

    if (!this.nav) {
      this.nav = document.createElement('div');
      this.nav.className = 'jdgm-dots';
      this.nav.setAttribute('role', 'tablist');
      this.nav.setAttribute('aria-label', 'Navigation des avis');
      this.content.after(this.nav);
    }

    this.nav.innerHTML = '';
    this.dots = Array.from({ length: this.pageCount }, (_, page) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'jdgm-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Aller au groupe d'avis ${page + 1}`);
      dot.addEventListener('click', () => this.goToPage(page));
      this.nav.append(dot);
      return dot;
    });

    // Une seule page : pas besoin de dots.
    this.nav.hidden = this.pageCount <= 1;
    this.syncActiveDot();
  }

  /**
   * Highlight the dot matching the active card.
   */
  syncActiveDot() {
    if (!this.dots.length) {
      return;
    }

    const page = Math.min(Math.floor(this.currentIndex / this.perPage), this.pageCount - 1);
    this.dots.forEach((dot, index) => {
      const isActive = index === page;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      dot.tabIndex = isActive ? 0 : -1;
    });
  }

  /**
   * Navigate to a given page by stepping through Judge.me's own nav API until
   * the target card index is reached.
   * @param {number} page
   */
  async goToPage(page) {
    if (this.moving || typeof window.jdgmNextCard !== 'function') {
      return;
    }

    const target = Math.min(page * this.perPage, this.cardsCount - 1);
    if (this.currentIndex === target) {
      return;
    }

    this.moving = true;
    let guard = 0;
    while (this.currentIndex !== target && guard < this.cardsCount + 3) {
      const before = this.currentIndex;
      if (before < target) {
        window.jdgmNextCard(this.blockId);
      } else {
        window.jdgmPreviousCard(this.blockId);
      }

      await this.waitForIndexChange(before);
      guard += 1;
    }
    this.moving = false;
    this.syncActiveDot();
  }

  /**
   * Resolve once the active card index differs from `before` (or after a
   * timeout, as a safety net when a boundary is reached).
   * @param {number} before
   * @returns {Promise<void>}
   */
  waitForIndexChange(before) {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) {
          return;
        }
        done = true;
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      };
      const observer = new MutationObserver(() => {
        if (this.currentIndex !== before) {
          finish();
        }
      });
      observer.observe(this.track, {
        attributes: true,
        subtree: true,
        attributeFilter: ['class'],
      });
      const timer = setTimeout(finish, 900);
    });
  }
}

/**
 * Mount the dots on every Judge.me Cards Carousel once its cards are rendered.
 *
 * Le bloc est injecté par l'app Judge.me (pas de `data-component` possible) et
 * ses cartes arrivent de façon asynchrone : on observe le DOM jusqu'à ce que le
 * widget soit peuplé, puis on instancie le composant.
 */
export function mountJudgemeCarouselDots() {
  if (!document.body) {
    document.addEventListener('DOMContentLoaded', mountJudgemeCarouselDots, { once: true });
    return;
  }

  const mounted = new WeakSet();

  const tryMount = () => {
    document.querySelectorAll('.jdgm-cards-carousel').forEach((el) => {
      if (mounted.has(el)) {
        return;
      }
      const hasCards = el.querySelector(
        '.jdgm-videos-container .jdgm-carousel-item:not(.jdgm-clone-before):not(.jdgm-clone-after)',
      );
      if (!hasCards) {
        return;
      }
      mounted.add(el);
      const instance = new JudgemeCarouselDots(el);
      instance.$mount();
    });
  };

  tryMount();
  const observer = new MutationObserver(tryMount);
  observer.observe(document.body, { childList: true, subtree: true });
}
